import { IfpApplicationState, IfpUserState } from './types'

/**
 * @returns error string if the application is not cancellable, else null
 */
export const checkIfpApplicationCanBeCancelled = ({
  applicationState,
  isPrimaryApplicant,
  userState,
}: {
  applicationState: IfpApplicationState
  isPrimaryApplicant: boolean
  userState?: IfpUserState
}): string | null => {
  if (isPrimaryApplicant) {
    if (
      ![
        IfpApplicationState.Draft,
        IfpApplicationState.WaitingForCoDebtor,
        IfpApplicationState.ReturnedByCoDebtor,
      ].includes(applicationState)
    ) {
      return `Primary cannot cancel when application is in state '${applicationState}'`
    }
  } else {
    if (applicationState !== IfpApplicationState.WaitingForCoDebtor) {
      return `Co-debtor cannot cancel when application is in state '${applicationState}'`
    }

    if (userState !== undefined) {
      return `Co-debtor cannot cancel the application if they have already performed an action: ${userState}`
    }
  }
  return null
}

/**
 * @returns error string if the application cannot be accepted nor submitted, else null
 */
export const checkIfpApplicationCanBeAcceptedOrSubmitted = ({
  applicationState,
  isPrimaryApplicant,
}: {
  applicationState: IfpApplicationState
  isPrimaryApplicant: boolean
}): string | null => {
  if (
    isPrimaryApplicant &&
    ![IfpApplicationState.Draft, IfpApplicationState.ReturnedByCoDebtor].includes(applicationState)
  ) {
    return `Application can not be submitted when in state '${applicationState}'`
  }

  if (!isPrimaryApplicant && applicationState !== IfpApplicationState.WaitingForCoDebtor) {
    return `Application can only be accepted when in state '${IfpApplicationState.WaitingForCoDebtor}'. Current state '${applicationState}'`
  }

  return null
}

/**
 * @returns error string if the application cannot be returned, else null
 */
export const checkIfpApplicationCanBeReturned = ({
  applicationState,
  isPrimaryApplicant,
}: {
  applicationState: IfpApplicationState
  isPrimaryApplicant: boolean
}): string | null => {
  if (isPrimaryApplicant) {
    return 'Primary applicant cannot return their own application'
  }

  if (applicationState !== IfpApplicationState.WaitingForCoDebtor) {
    return `Application can only be returned when in state '${IfpApplicationState.WaitingForCoDebtor}'. Current state '${applicationState}'`
  }

  return null
}

/**
 * @returns error string if the application cannot be updated, else null
 */
export const checkIfpApplicationDataCanBeUpdated = ({
  applicationState,
  isPrimaryApplicant,
  newState = applicationState,
}: {
  applicationState: IfpApplicationState
  isPrimaryApplicant: boolean
  newState?: IfpApplicationState
}): string | null => {
  if (!isPrimaryApplicant) {
    return 'Application data can only be updated by primary'
  }

  if (![IfpApplicationState.Draft, IfpApplicationState.ReturnedByCoDebtor].includes(applicationState)) {
    return `Application data cannot be updated when in state: '${applicationState}'`
  }

  if (newState !== applicationState) {
    return `Data update must not alter state. '${applicationState}' cannot become ${newState}`
  }

  return null
}

/**
 * @returns error string if the application is not in a sending state, else null
 */
const checkIfpApplicationHasBeenSent = (applicationState: IfpApplicationState): string | null => {
  if (![IfpApplicationState.Sending, IfpApplicationState.WaitingForResend].includes(applicationState)) {
    return 'Application not in sending state'
  }

  return null
}

/**
 * @returns error string if the application cannot become UnavailableOther, else null
 */
export const checkIfpApplicationCanBeUnavailableOther = (applicationState: IfpApplicationState): string | null => {
  if (
    ![
      IfpApplicationState.Draft,
      IfpApplicationState.ReturnedByCoDebtor,
      IfpApplicationState.WaitingForCoDebtor,
    ].includes(applicationState)
  ) {
    return `Application cannot become ${IfpApplicationState.UnavailableOther} when in state: '${applicationState}'`
  }
  return null
}

/**
 * @returns error string if the application cannot become invalidated draft, else null
 */
const checkIfpApplicationCanBeInvalidatedDraft = (applicationState: IfpApplicationState): string | null => {
  return applicationState === IfpApplicationState.Draft
    ? null
    : `${applicationState} is not ${IfpApplicationState.Draft}, thus cannot become ${IfpApplicationState.UnavailableDraftInvalidated}`
}

/**
 * @returns error string if the application cannot be sent, else null
 */
const checkIfpApplicationCanBeSent = ({
  applicationState,
  isPrimaryApplicant,
  hasUserAccepted,
}: {
  applicationState: IfpApplicationState
  isPrimaryApplicant: boolean
  hasUserAccepted?: boolean
}): string | null => {
  if (!hasUserAccepted) {
    return 'User must accept application before it can be sent'
  }
  if (
    (isPrimaryApplicant && applicationState !== IfpApplicationState.Draft) ||
    (!isPrimaryApplicant && applicationState !== IfpApplicationState.WaitingForCoDebtor)
  ) {
    return `Application in state ${applicationState} cannot be sent`
  }
  return null
}

export const checkIfpApplicationStateChangeAllowed = ({
  currentState,
  newState,
  isPrimaryApplicant,
  userState,
}: {
  currentState: IfpApplicationState
  newState: IfpApplicationState
  isPrimaryApplicant: boolean
  userState?: IfpUserState
}): string | null => {
  const stateChangeChecks: Record<IfpApplicationState, () => string | null> = {
    [IfpApplicationState.UnavailableOther]() {
      return checkIfpApplicationCanBeUnavailableOther(currentState)
    },
    [IfpApplicationState.UnavailableDraftInvalidated]() {
      return checkIfpApplicationCanBeInvalidatedDraft(currentState)
    },
    [IfpApplicationState.WaitingForCoDebtor]() {
      return checkIfpApplicationCanBeAcceptedOrSubmitted({
        applicationState: currentState,
        isPrimaryApplicant,
      })
    },
    [IfpApplicationState.ReturnedByCoDebtor]() {
      return currentState === IfpApplicationState.ReturnedByCoDebtor && isPrimaryApplicant
        ? checkIfpApplicationDataCanBeUpdated({ applicationState: currentState, isPrimaryApplicant, newState })
        : checkIfpApplicationCanBeReturned({
            applicationState: currentState,
            isPrimaryApplicant,
          })
    },
    [IfpApplicationState.Cancelled]() {
      return checkIfpApplicationCanBeCancelled({
        applicationState: currentState,
        isPrimaryApplicant,
        userState,
      })
    },
    [IfpApplicationState.Sending]() {
      return checkIfpApplicationCanBeSent({
        applicationState: currentState,
        isPrimaryApplicant,
        hasUserAccepted: userState === IfpUserState.Accepted,
      })
    },
    [IfpApplicationState.WaitingForResend]() {
      return checkIfpApplicationHasBeenSent(currentState)
    },
    [IfpApplicationState.Rejected]() {
      return checkIfpApplicationHasBeenSent(currentState)
    },
    [IfpApplicationState.Pending]() {
      return checkIfpApplicationHasBeenSent(currentState)
    },
    [IfpApplicationState.Accepted]() {
      return checkIfpApplicationHasBeenSent(currentState)
    },
    [IfpApplicationState.Draft]() {
      return checkIfpApplicationDataCanBeUpdated({
        applicationState: currentState,
        isPrimaryApplicant,
        newState,
      })
    },
  }

  return stateChangeChecks[newState]()
}

export const checkIfpApplicationMultipleCanExist = ({
  applicationState,
  isPrimaryApplicant,
}: {
  applicationState: IfpApplicationState
  isPrimaryApplicant: boolean
}): string | null => {
  const allowedStatesForMultipleApplications = [
    IfpApplicationState.Cancelled,
    IfpApplicationState.Draft, // allowed only for when user is not application's primary
    IfpApplicationState.Rejected,
    IfpApplicationState.Accepted,
    IfpApplicationState.UnavailableOther,
    IfpApplicationState.UnavailableDraftInvalidated,
    IfpApplicationState.Pending,
    IfpApplicationState.WaitingForResend,
  ]
  if (
    (isPrimaryApplicant && applicationState === IfpApplicationState.Draft) ||
    !allowedStatesForMultipleApplications.includes(applicationState)
  ) {
    return 'Application for loan already exists'
  }

  return null
}
